// Piezas visuales de la vista de soporte.
//
// El criterio de diseño: cada grupo de datos vive en su propia tarjeta con un
// ícono, para que quien atiende una llamada ubique de un vistazo dónde mirar
// ("pagos", "dispositivos", "cuenta") sin leer los títulos. La densidad importa
// tanto como la estética — es una pantalla de trabajo, no un dashboard.

import { ReactNode, useState } from 'react';
import { Check, ChevronDown, Copy, type LucideIcon } from 'lucide-react';
import type { HelpId } from '@/help/targets';

export function DataCard({
  icon: Icon,
  title,
  count,
  actions,
  children,
  className = '',
  helpId,
}: {
  icon: LucideIcon;
  title: string;
  count?: number | string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  /**
   * Punto del manual que explica esta tarjeta. Lo usan el modo señalar y los
   * recorridos guiados. Va como prop y no como atributo suelto en el call-site
   * para que el tipo obligue a que el id exista en el registro de ayuda.
   */
  helpId?: HelpId;
}): JSX.Element {
  return (
    <section
      data-help={helpId}
      className={`bg-surface dark:bg-surface-dark rounded-xl border border-surface-border dark:border-surface-dark-border overflow-hidden ${className}`}
    >
      <header className="px-4 py-3 border-b border-surface-border dark:border-surface-dark-border flex items-center gap-2">
        <Icon size={16} className="shrink-0 text-brand" aria-hidden="true" />
        <h2 className="text-sm font-semibold flex-1">
          {title}
          {count !== undefined ? (
            <span className="ml-2 text-xs font-normal text-ink-muted tabular-nums">{count}</span>
          ) : null}
        </h2>
        {actions}
      </header>
      {children}
    </section>
  );
}

/** Dato etiquetado. `mono` para IDs, `wrap` para lo que puede ser largo. */
export function Field({
  label,
  value,
  mono = false,
  copyable = false,
  hint,
}: {
  label: string;
  value: ReactNode;
  mono?: boolean;
  copyable?: string | false;
  hint?: string;
}): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] uppercase tracking-wide text-ink-subtle" title={hint}>
        {label}
      </dt>
      <dd
        className={`text-sm flex items-center gap-1 min-w-0 ${mono ? 'font-mono text-xs' : ''}`}
      >
        <span className="truncate">{value ?? '—'}</span>
        {copyable ? <CopyButton value={copyable} label={label} /> : null}
      </dd>
    </div>
  );
}

/**
 * Copiar al portapapeles. Existe porque el 90% de lo que hace soporte con un
 * user_id o un CAE es pegarlo en otro lado (un ticket, un chat con el partner,
 * el panel de la pasarela), y seleccionarlo a mano en un `font-mono` chiquito
 * es donde se pierde el tiempo y se cometen errores de un caracter.
 */
export function CopyButton({ value, label }: { value: string; label?: string }): JSX.Element {
  const [copied, setCopied] = useState(false);

  return (
    <button
      type="button"
      title={`Copiar ${label ?? 'valor'}`}
      aria-label={`Copiar ${label ?? 'valor'}`}
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        });
      }}
      className="shrink-0 p-1 rounded text-ink-subtle hover:text-brand hover:bg-brand/10 focus-visible:ring-2 focus-visible:ring-brand-light"
    >
      {copied ? (
        <Check size={13} aria-hidden="true" className="text-status-success" />
      ) : (
        <Copy size={13} aria-hidden="true" />
      )}
    </button>
  );
}

/** Cifra grande de la franja de resumen. */
export function StatCard({
  icon: Icon,
  label,
  value,
  detail,
  tone = 'neutral',
}: {
  icon: LucideIcon;
  label: string;
  value: ReactNode;
  detail?: ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'error';
}): JSX.Element {
  const toneCls =
    tone === 'success'
      ? 'text-status-success'
      : tone === 'warning'
        ? 'text-status-warning'
        : tone === 'error'
          ? 'text-status-error'
          : 'text-ink dark:text-ink-inverted';

  return (
    <div className="bg-surface dark:bg-surface-dark rounded-xl border border-surface-border dark:border-surface-dark-border p-3 min-w-0">
      <div className="flex items-center gap-1.5 text-ink-subtle">
        <Icon size={13} className="shrink-0" aria-hidden="true" />
        <span className="text-[11px] uppercase tracking-wide truncate">{label}</span>
      </div>
      <p className={`mt-1 text-lg font-semibold leading-tight truncate ${toneCls}`}>{value}</p>
      {detail ? <p className="text-xs text-ink-muted truncate">{detail}</p> : null}
    </div>
  );
}

/** Fila que se despliega. El chevron rota; toda la cabecera es el botón. */
export function ExpandableRow({
  summary,
  children,
  defaultOpen = false,
}: {
  summary: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-surface-border dark:border-surface-dark-border last:border-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full text-left px-4 py-3 flex items-start gap-2 hover:bg-surface-muted dark:hover:bg-surface-dark-muted focus-visible:ring-2 focus-visible:ring-brand-light"
      >
        <ChevronDown
          size={16}
          aria-hidden="true"
          className={`shrink-0 mt-0.5 text-ink-subtle transition-transform ${open ? 'rotate-180' : ''}`}
        />
        <div className="flex-1 min-w-0">{summary}</div>
      </button>
      {open ? (
        <div className="px-4 pb-4 pl-10 bg-surface-muted/40 dark:bg-surface-dark-muted/40">
          {children}
        </div>
      ) : null}
    </div>
  );
}

export function Pill({
  children,
  tone = 'neutral',
  title,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'error' | 'info' | 'brand';
  title?: string;
}): JSX.Element {
  const cls = {
    neutral: 'bg-ink-muted/15 text-ink-muted',
    success: 'bg-status-success/15 text-status-success',
    warning: 'bg-status-warning/15 text-status-warning',
    error: 'bg-status-error/15 text-status-error',
    info: 'bg-status-info/15 text-status-info',
    brand: 'bg-brand/15 text-brand',
  }[tone];

  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5 font-medium whitespace-nowrap ${cls}`}
    >
      {children}
    </span>
  );
}

export function EmptyNote({ children }: { children: ReactNode }): JSX.Element {
  return <p className="px-4 py-6 text-sm text-ink-muted text-center">{children}</p>;
}
